import GooglePayApplePay from '@/assets/img/google-pay-apple-pay.webp';
import type { InfoSliderItem } from '../types/card.type';

export {
  CARD_COUNTRIES,
  CARD_CURRENCIES,
  CARD_PARTNERS,
  CARD_STATUS,
  CARD_TARIFF_URLS,
  CARD_TYPE_PRODUCT,
  DELETE_RESTRICTED_CARD_ALERT,
  OFFSHORE_CARD_CONFIG,
  OFFSHORE_COUNTRY_CODE,
  OFFSHORE_NOT_ISSUED_STATUSES,
  RETRY_CREATION_TOOLTIP_MESSAGE,
  SALES_EXECUTIVE_CONTACT_MESSAGE,
  STANDARD_CARD_COUNTRIES,
} from '@/shared/smartCard/constants/smartCard.constant';

export const CARD_CONFIG = {
  MAX_AUTO_CREATE_ATTEMPTS: 3,
  CAROUSEL_AUTOPLAY_DURATION: 3000,
  DETAIL_COUNTDOWN_SECONDS: 59,
} as const;

export const OFFSHORE_VERIFICATION_STEP = {
  NOT_STARTED: 1,
  IN_PROGRESS: 2,
  VERIFIED: 3,
} as const;

export const JUMIO_WORKFLOW_STATUS = {
  INITIATED: 'INITIATED',
  ACQUIRED: 'ACQUIRED',
  PROCESSED: 'PROCESSED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
} as const;

export const OFFSHORE_JUMIO_MESSAGES = {
  WORKFLOW_ERROR: 'No pudimos iniciar el proceso de verificación.',
  MISSING_TOKEN:
    'No pudimos iniciar la verificación de identidad. Intenta nuevamente.',
  SDK_FAILED:
    'No pudimos completar la verificación de identidad. Intenta nuevamente.',
} as const;

export const OFFSHORE_POLLING_MESSAGES = {
  ALREADY_HAS_CARD: 'Ya tienes una Smart Card en dólares activa.',
  NO_ACTIVITY:
    'No detectamos actividad en la verificación. Intenta nuevamente.',
} as const;

export const OFFSHORE_POLLING_CONFIG = {
  MAX_ATTEMPTS: 180,
  POLLING_DELAY_MS: 5000,
  MAX_CONSECUTIVE_ERRORS: 5,
  MAX_DURATION_MS: 15 * 60 * 1000,
  STEP_EVALUATION: 2,
} as const;

export const CARD_TYPES = {
  DEBIT: 'Débito',
  PREPAID: 'Prepago',
} as const;

/**
 * Países habilitados para el flujo Smart Card Off Shore (Virtual en dólares).
 * Solo CHL entra al nuevo landing / entry drawer con el flag
 * `feature_offshore_smartcard_chl`. CO conserva su camino directo actual.
 */
export const OFFSHORE_ELIGIBLE_COUNTRIES = ['CL'] as const;

/**
 * Contenido del landing "Virtual en dólares" (REQ-5).
 */
export const OFFSHORE_LANDING_CONTENT = {
  title: 'Tu saldo en dólares, ahora en tus manos',
  subtitle:
    'Paga directamente con tus saldos internacionales usando Apple Pay o Google Wallet. Sin tocar tu moneda local.',
  image: GooglePayApplePay,
  benefits: [
    {
      icon: 'regular credit-card',
      title: '100% Virtual',
      description:
        'Lista para usar al instante en tu billetera digital. Sin plásticos ni envíos.',
    },
    {
      icon: 'regular dollar-sign',
      title: 'Directo de tus Dólares',
      description:
        'Tus pagos se descuentan de tu saldo USD. No usa tu saldo en moneda local.',
    },
    {
      icon: 'regular globe',
      title: 'Emitida en el Extranjero',
      description:
        'Ideal para viajes y compras online internacionales sin fricción.',
    },
  ],
  comparisonRows: [
    {
      label: 'Saldo que utiliza',
      traditional: 'Prioriza moneda local',
      dollars: 'Prioriza Dólar (USD)',
    },
    {
      label: 'Emisión',
      traditional: 'Local (Tu país)',
      dollars: 'Internacional (Extranjero)',
    },
    {
      label: 'Uso ideal',
      traditional: 'Compras diarias locales',
      dollars: 'Viajes, suscripciones y compras online',
    },
  ],
} as const;

export const CARD_MESSAGES = {
  CHILE:
    'Utiliza tu tarjeta para pagar tus plataformas tecnológicas y publicidad online: Facebook, Microsoft, Hubspot, Paypal, Uber, OpenAI, Amazon, LinkedIn y muchas otras.',
  DEFAULT:
    'Utiliza tu tarjeta para pagar tus plataformas tecnológicas y publicidad online: Facebook, Google, Microsoft, Hubspot, Paypal, Uber, OpenAI, Amazon, LinkedIn y muchas otras.',
} as const;

export const DIGITAL_VAT_SLIDE = {
  title: '¿Conoces qué es el IVA digital?',
  description:
    'Por ley, el pago de proveedores digitales extranjeros tiene un IVA del 19%. Conoce qué servicios están afectos aquí.',
  img: 'price-tags' as const,
  redirectLink: 'https://www.sii.cl/vat/neni_eng.html',
  redirectText: 'Conoce más',
} as const;

export const OFFSHORE_TERMS_URLS = {
  GLOBAL_81_COLOMBIA:
    'https://global-files-statics.s3.us-east-1.amazonaws.com/TyC+Global+81+Colombia.pdf',
} as const;

export const CARD_SLIDER_INFO: InfoSliderItem[] = [
  {
    title: 'Tu Smart Card virtual.',
    description: '',
    img: 'information' as const,
  },
  {
    title: 'Paga todas tus suscripciones con tu Smart Card Virtual.',
    description: '',
    img: 'card' as const,
  },
  {
    title: 'Con Tu Smart Card paga en dólares, te cobramos en dólares.',
    description:
      'Olvida las comisiones o costos por tipo de cambio: compra la divisa con que pagarás cuando tú quieras y luego paga con ese saldo.',
    img: 'dollar-currency' as const,
  },
] as const;

export const POMELO_SECURE_DATA_URL = import.meta.env
  .VITE_POMELO_SECURE_DATA_URL;

export const POMELO_STYLES = {
  DRAWER: `${import.meta.env.VITE_S3_ASSETS_URL}/assets/styles/pomelo/cardDetailsDrawerStyle.css`,
  LIST: `${import.meta.env.VITE_S3_ASSETS_URL}/assets/styles/pomelo/pomelo-list-styles.css`,
} as const;

export const CARD_LIST_PAGE_SIZE = 10;

export const OFFSHORE_LOOKUP_PAGE_SIZE = 100;

export const DEACTIVATING_SLIDE_INDEX = 0;

export const LIST_TO_SHOW = {
  ACTIVE: 'ACTIVE',
  TEMPORAL: 'TEMPORAL',
  PENDING: 'PENDING',
} as const;

export const CARD_LIST_OPTIONS = [
  { label: 'Recurrentes', value: LIST_TO_SHOW.ACTIVE },
  { label: 'Temporales', value: LIST_TO_SHOW.TEMPORAL },
  { label: 'Pendientes', value: LIST_TO_SHOW.PENDING },
];

export const PENDING_INLINE_MESSAGE =
  'La persona asignada debe completar la información para finalizar su creación. Recuerda que la invitación dura una semana; pasado ese tiempo, tendrás que enviarla de nuevo.';

export const RETRY_CREATION_ERROR_MESSAGE =
  'Ha ocurrido un error al reintentar la creación de la tarjeta.';

export const DEFAULT_CARD_ALIAS = 'Tarjeta virtual' as const;
